import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { IOrganization, IUser } from "app/auth/user.model";
import { Events, PopoverController } from "ionic-angular";
import { AsmtTypeLookupCode } from "models/assessment/assessment-type";
import Company from "models/company-details/company";
import SortOptions from "models/content-wrapper/sort-options";
import { AlertMessage } from "models/notificiations/websocket-message";
import * as momentTZ from "moment-timezone";
import { CompanyManagementService } from "providers/company-management-service/company-management-service";
import { MedicationDataService } from "providers/order-data-service/medications-data-service";
import { UserProfileService } from "providers/user-profile/user-profile.service";
import { UtilityService } from "providers/utility-service/utility-service";
import { Subject, Subscription } from "rxjs";
import { of } from "rxjs/observable/of";
import { catchError, retry } from "rxjs/operators";
import { AccessControlService } from "../../../app/access-control/access-control-service";
import { CUBE_MENU_PERMISSIONS } from "../../../app/access-control/common-permission-asks";
import { hasAccess } from "../../../app/access-control/permission-verification";
import { AuthService } from "../../../app/auth/auth.service";
import HeaderData from "../../../mocks/mock-header-data";
import { BasePage } from "../../../pages/base-page";
import { AlertsDataService } from "../../../providers/alerts-data-service/alerts-data-service";
import {
  AppStatus,
  ConnectionStatusEnum,
} from "../../../providers/app-status/app-status";
import { AlertWebSocketService } from "../../../providers/appsync/alter-notifications-websocket.service";
import { CalendarService } from "../../../providers/calendar-service/calendar-service";
import { MobileUtilsProvider } from "../../../providers/mobile-utils/mobile-utils";
import { NavBarServiceProvider } from "../../../providers/nav-bar-service/nav-bar-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { NavigationService } from "../../navigation.service";
import { AppsPopoverPage } from "../apps-popover/apps-popover";
import { CalendarPopoverComponent } from "../calendar-popover/calendar-popover";
import { UserPopoverPage } from "../user-popover/user-popover";
import { CubeApplication, generateApplications } from "./cube-application";
import { NavigationHeaderData } from "./navigation-header-data";

@Component({
  selector: "navigation-header",
  templateUrl: "navigation-header.html",
})
export class NavigationHeaderComponent
  extends BasePage
  implements OnInit, OnDestroy
{
  private eventSubscription: Subscription;
  isLoadingApps: boolean = false;

  @Input() sectionTitle: string;
  @Input() mainTitle: string;
  @Input() patientChartId: number;

  timerDestroy = new Subject();
  private joinSubs: Subscription;
  private userInfoSub: Subscription;
  headerData: NavigationHeaderData;
  alertsCount: number = 0;
  calendarActiveStatus: any;
  isMobilePlatform: boolean;
  appSyncStatus: ConnectionStatusEnum;
  connectionStatusEnum = ConnectionStatusEnum;
  checkIfMobileOnly: boolean = true;
  _alertsCount: any;

  organizations: IOrganization[] = [];
  isPatientCalendarToggleOn: boolean = false;
  isLegacyBillingToggleOn: boolean = false;
  isNewAlertToggleOn: boolean = false;

  showNavbar: boolean = false;
  selectedOrganization: number | undefined;
  selectedOrgType: string | undefined;
  subscriptions: Subscription[] = [];
  alertsSubscriptions: Subscription[] = [];
  spinnerCounter: number = 0;
  spinner: any = null;

  companyData: Company[] = [];
  currentPage: number = 0;
  totalPageCount: number = 0;
  search: string;
  pageSize: number = 10;
  request: any = {};
  sortOptions: SortOptions = new SortOptions("name", "asc");
  messages: AlertMessage[] = [];
  connectionStatus: "connecting" | "connected" | "disconnected" | "failed" =
    "disconnected";
  channels: string[];

  constructor(
    private accessCntrl: AccessControlService,
    private authSvc: AuthService,
    public calendarService: CalendarService,
    private popoverCtrl: PopoverController,
    public navData: HeaderData,
    public navSvc: NavigationService,
    public stateData: StateManagementService,
    public mobileUtilsProvider: MobileUtilsProvider,
    public events: Events,
    public appStatus: AppStatus,
    public navBarServiceProvider: NavBarServiceProvider,
    private userProfileService: UserProfileService,
    public utilityService: UtilityService,
    private cdr: ChangeDetectorRef,
    private medicationDataService: MedicationDataService,
    protected companyManagementService: CompanyManagementService,
    private alertWebSocketService: AlertWebSocketService,
    public alertsData: AlertsDataService
  ) {
    super(stateData);
    this.events.subscribe("user:organizations", (user: IUser) => {
      if (user) {
        this.utilityService.increaseSpinnerCount("Fetching Organizations...");
        this.userProfileService
          .getUserOrganizations(user.userId.toString())
          .pipe(
            retry(1),
            catchError((error) => {
              console.error("Error fetching user organizations:", error);
              const fallbackUser = this.authSvc.getUser();
              if (fallbackUser && fallbackUser.selectedOrgType) {
                this.selectedOrgType = fallbackUser.selectedOrgType;
                this.selectedOrganization = Number(fallbackUser.tenantId);
                this.showNavbar = true;
                this.cdr.markForCheck();
                this.utilityService.presentCustomWarningToast(
                  "Unable to load organizations. Some features may be limited."
                );
              }
              return of([]);
            })
          )
          .subscribe(
            (data) => {
              this.utilityService.decreaseSpinnerCount();
              if (data && data["hits"]) {
                const result = data["hits"][0];
                if (result && result.orgRoles && result.orgRoles.length > 0) {
                  this.organizations = result.orgRoles as IOrganization[];
                  this.organizations.sort((a, b) =>
                    a.orgName.localeCompare(b.orgName)
                  );
                  let user = this.authSvc.getUser();
                  user.organizations = this.organizations;
                  // OPS-2858: same response, one more field. Coerced to a real boolean
                  // so an absent attribute fails closed rather than staying undefined.
                  user.isSupportUserAdmin = result.isSupportUserAdmin === true;
                  this.authSvc.setUser(user);
                  this.authSvc.setLoggedInUser(user);
                  this.authSvc.UserInfoChange.next(user);
                  this.showNavbar = true;
                  this.updateOrganizations(user);
                  setTimeout(() => {
                    this.events.publish("user:updateorganizations", user);
                  }, 0);
                  this.cdr.markForCheck();
                }
              }
            },
            () => this.utilityService.decreaseSpinnerCount(),
            () => this.utilityService.decreaseSpinnerCount()
          );
      }
    });

    this.checkIfMobileOnly = mobileUtilsProvider.checkIfMobile();
    this.calendarActiveStatus = window.localStorage["calendarStatus"];
    this.sectionTitle = this.stateData.getSectionId();
    this.appSyncStatus = this.appStatus.getAppStatus();
    this.stateData.getSectionIdObserver().subscribe((data) => {
      this.sectionTitle = data;

      this.stateData.showPalliativeV1_5.subscribe((x) => {
        if (x !== null && x) {
          let patientCareType = this.stateData.getPatientCareType();
          if (patientCareType && patientCareType == "Palliative") {
            this.updateSectionLabelPal1_5();
          } else {
            this.stateData
              .getPatientCareTypeObserver()
              .subscribe((careType: String) => {
                if (careType == "Palliative") {
                  this.updateSectionLabelPal1_5();
                }
              });
          }
        }
      });
    });
    this.stateData.setAssessmentType(this.navData.assessmentType);
    this.mainTitle = this.stateData.getAssessmentType();
    this.stateData.getAssessmentTypeObserver().subscribe((data) => {
      this.mainTitle = data;
    });

    this.patientChartId = parseInt(this.stateData.getChartId());
    this.stateData.getChartIdObserver().subscribe((data) => {
      this.patientChartId = parseInt(data);
    });

    this.events.subscribe("networkchange", (eventData) => {
      this.appSyncStatus = eventData.status;
    });
  }

  updateSectionLabelPal1_5 = () => {
    if (
      this.sectionTitle == "preferences" &&
      this.stateData.navigationStatusData.asmtTypeLookupCode !=
        AsmtTypeLookupCode.EVALUATION
    ) {
      this.sectionTitle = "advancedCarePlan";
    }
  };

  isLoggedIn() {
    return this.authSvc.isAuthenticated();
  }

  ngOnDestroy() {
    this.cleanUp();
    this.events.unsubscribe("user:organizations");
    this.alertsSubscriptions.forEach((sub) => sub.unsubscribe());
  }

  private cleanUp() {
    if (this.timerDestroy) {
      this.timerDestroy.next();
      this.timerDestroy.complete();
    }
    if (this.eventSubscription) {
      this.eventSubscription.unsubscribe();
    }
    this.subscriptions.forEach((val) => val.unsubscribe());
  }

  ngOnInit() {
    this.cleanUp();
    let data = this.navData;
    this.headerData = data;
    this.stateData.setUserData(data);

    this.stateData.isPatientCalendarToggleOn.subscribe((value) => {
      this.isPatientCalendarToggleOn = !!value;
    });

    this.stateData.showLegacyBilling.subscribe((value) => {
      this.isLegacyBillingToggleOn = value;
    });


    this.stateData.showNewAlerts.subscribe((value) => {
      this.isNewAlertToggleOn = !!value;
      if (this.isNewAlertToggleOn) {
        let user = this.authSvc.getUser();
        if (user) this.setChannels(user);
      }
    });

    this.alertsSubscriptions.push(
      this.alertWebSocketService.connectionStatus$.subscribe((status) => {
        this.connectionStatus = status;
      })
    );

    this.alertsSubscriptions.push(
      this.alertWebSocketService.countNotifications$.subscribe((count) => {
        this.alertsCount += count;
      })
    );

    this.alertsSubscriptions.push(
      this.alertWebSocketService.setNotificationsCount$.subscribe((count) => {
        if (count !== 0 && this.alertsCount !== count) this.alertsCount = count;
      })
    );
  }

  private setChannels(user: IUser) {
    this.channels = [user.userId.toString()];

    if (this.channels && this.channels.length > 0) {
      this.channels.forEach((channel) => {
        this.alertWebSocketService.connect(channel);
      });
    }

    this.alertsData.getAlertsCount().subscribe((count: number) => {
      this.alertsCount = count;
    });
  }

  private updateOrganizations(user): void {
    this.selectedOrganization = undefined;
    this.selectedOrgType = undefined;
    this.selectedOrganization = Number(user.tenantId);
    this.selectedOrgType = user.selectedOrgType;
    this.loadFilters(user);
    this.getLocationTimeZone();
  }

  loadFilters(user: any) {
    this.request = {};
    this.request.mustFields = [];
    this.request.entityType = this.selectedOrgType;
    this.request.mustFields.push({
      name: "orgId",
      value: user.tenantId.toString(),
    });
  }

  getLocationTimeZone() {
    this.companyManagementService.getCompanyList(this.request).subscribe(
      (response) => {
        let user = this.authSvc.getUser();
        if (!user) {
          return;
        }
        if (response && response["hits"] && response["totalHits"] > 0) {
          this.companyData = response["hits"] as Company[];
          let company = this.companyData.find(
            (company) => +company.orgId === this.selectedOrganization
          );
          if (!company) {
            // NOVA-1600: surface the previously-silent fallback. No matching
            // company means we can't resolve the real org timezone, so dates
            // would render in the viewer's browser timezone.
            console.error(
              "Org timezone lookup: no company matched org " +
                this.selectedOrganization +
                "; falling back to browser timezone."
            );
            user.timeZoneOrg = momentTZ.tz.guess();
          } else {
            user.timeZoneOrg = company.timezone;
            user.organizations = this.addVendorInfoToSelectedOrg(
              user.organizations,
              this.selectedOrganization
            );
            // Extract phone number from company contact
            if (
              company.contact &&
              company.contact.phones &&
              company.contact.phones.length > 0
            ) {
              // Get the first phone number (preferably OFFICE type)
              const officePhone = company.contact.phones.find(
                (phone) => phone.type === "OFFICE"
              );
              user.tenantPhone = officePhone
                ? officePhone.number
                : company.contact.phones[0].number;
            }
          }
        } else {
          // NOVA-1600: previously this path did nothing — user.timeZoneOrg was
          // never set, so the ribbon/Status surfaces kept guessing the browser
          // timezone forever (and never recomputed). Log and fall back loudly.
          console.error(
            "Org timezone lookup returned no results for org " +
              this.selectedOrganization +
              "; falling back to browser timezone."
          );
          user.timeZoneOrg = momentTZ.tz.guess();
        }
        // NOVA-1600: always publish so subscribers (ribbon, Status tab)
        // recompute once the timezone is resolved.
        this.authSvc.setUser(user);
        this.authSvc.setLoggedInUser(user);
        this.authSvc.UserInfoChange.next(user);
      },
      (error) => {
        // NOVA-1600: the empty error callback used to swallow lookup failures
        // silently, leaving every date surface stuck on the browser-timezone
        // guess. Log it and still publish a resolved (fallback) timezone.
        console.error(
          "Org timezone lookup failed; falling back to browser timezone.",
          error
        );
        let user = this.authSvc.getUser();
        if (!user) {
          return;
        }
        user.timeZoneOrg = momentTZ.tz.guess();
        this.authSvc.setUser(user);
        this.authSvc.setLoggedInUser(user);
        this.authSvc.UserInfoChange.next(user);
      }
    );
  }

  addVendorInfoToSelectedOrg(
    organizations: IOrganization[],
    selectedOrgId: number
  ): IOrganization[] {
    for (let org of organizations) {
      if (Number(org.orgId) === selectedOrgId) {
        org.vendors =
          this.companyData.find((cmp) => cmp.orgId === org.orgId)?.vendors ||
          null;
        break;
      }
    }

    return organizations;
  }

  goToAlertsAndNotifications(id: string) {
    this.navSvc.goToInternalPage(id, {});
  }

  ionViewWillLeave() {
    this._alertsCount.unsubscribe();
    if (this.joinSubs) {
      this.joinSubs.unsubscribe();
    }

    if (this.userInfoSub) {
      this.userInfoSub.unsubscribe();
    }
  }

  presentCalendarPopover() {
    const popover = this.popoverCtrl.create(
      CalendarPopoverComponent,
      {},
      { cssClass: "apps-popover calendar-popover" }
    );

    popover.present();

    popover.onDidDismiss(() => {});
  }

  presentAppsPopover($event) {
    this.isLoadingApps = true;
    let applications = generateApplications();

    this.accessCntrl.getPermission(CUBE_MENU_PERMISSIONS).subscribe(
      (data) => {
        applications.forEach((y: CubeApplication) => {
          y.access = hasAccess(y.permission, data.permissions) || y.access;
        });

        const filtered = applications.filter((x: CubeApplication) => {
          return x.access;
        });

        const final = this.getFilteredCubeApplication(filtered);

        let popover = this.popoverCtrl.create(
          AppsPopoverPage,
          { applications: final },
          { cssClass: "apps-popover" }
        );
        popover.present({
          ev: $event,
        });
        popover.onDidDismiss(() => {
          this.isLoadingApps = false;
          this.cleanUp();
        });
      },
      () => {
        this.isLoadingApps = false;
      }
    );
  }

  private getFilteredCubeApplication(filtered: CubeApplication[]) {
    let final: CubeApplication[] = [];

    for (let i = 0; i < filtered.length; i++) {
      const x = filtered[i];
      if (x.name == "oldPatientsApp") {
        this.subscriptions.push(
          this.stateData.isPatientChart.subscribe((value) => {
            !value ? final.push(x) : null;
          })
        );
      } else if (x.name === "Patient") {
        this.subscriptions.push(
          this.stateData.isPatientChart.subscribe((value) => {
            value ? final.push(x) : null;
          })
        );
      } else if (x.name === "ResourceManagement") {
        this.subscriptions.push(
          this.stateData.showResourceManagement.subscribe((value) => {
            value ? final.push(x) : null;
          })
        );
      } else if (x.name === "CommunityBereavement") {
        this.subscriptions.push(
          this.stateData.showCommunityBereavement.subscribe((value) => {
            value ? final.push(x) : null;
          })
        );
      } else if (x.name == "LegacyBilling") {
        if (this.stateData.showLegacyBilling.getValue()) {
          final.push(x);
        }
      } else if (x.name == "OrderManagement") {
        this.subscriptions.push(
          this.stateData.showOrderEntryOM.subscribe((value) => {
            value ? final.push(x) : null;
          })
        );
      } else if (x.name == "Facilities") {
        this.subscriptions.push(
          this.stateData.newBillingFacility.subscribe((value) => {
            value ? null : final.push(x);
          })
        );
      } else if (x.name == "LegacyApp") {
        this.subscriptions.push(
          this.stateData.legacyApplicationsIconToggle.subscribe((value) => {
            value ? final.push(x) : null;
          })
        );
      } else if (x.name == "EPrescribe") {
        this.stateData.showEPrescribe.subscribe((value) => {
          if (value) {
            this.medicationDataService.getOrganization().subscribe((_) => {
              value ? final.push(x) : null;
            });
          }
        });
      } else if (x.name === "Analytics") {
        this.stateData.showAnalytics.subscribe((value) => {
          value ? final.push(x) : null;
        });
      } else if (x.name === "ReferralAndIntake") {
        this.stateData.showReferralAndIntake.subscribe((value) => {
          value ? final.push(x) : null;
        });
      } else if (x.name === "ChartMeds") {
        this.stateData.showChartMedsToggle.subscribe((value) => {
          value ? final.push(x) : null;
        });
      } else if (x.name === "WestFax") {
        this.stateData.isWestFaxToggleOn.subscribe((value) => {
          value ? final.push(x) : null;
        });
      } else if (x.name === "WorldViewFax") {
        this.stateData.isWorldViewFaxToggleOn.subscribe((value) => {
          value ? final.push(x) : null;
        });
      } else {
        final.push(x);
      }
    }

    return final;
  }

  presentUserPopover($event) {
    let popover = this.popoverCtrl.create(
      UserPopoverPage,
      { profileName: this.headerData.profileInfo.name },
      { cssClass: "user-popover" }
    );
    popover.present({
      ev: $event,
    });
    popover.onDidDismiss((mode) => {
      switch (mode) {
        case "sign-out-cognito":
          {
            this.alertWebSocketService.unsubscribeAll();
            this.authSvc.signOut();
          }
          break;
        default:
      }
    });
  }

  sendDashboard() {
    if (this.selectedOrgType === "LOCATION") {
      this.stateData.setChartId(null);
      this.stateData.setAssessmentType(null);
      this.stateData.setAssessmentId(null);
      // Clear patient context from sessionStorage to prevent stale IDs
      // from causing wrong-patient API calls on next patient navigation
      window.sessionStorage.removeItem("patientChartInfo");
      window.sessionStorage.removeItem("patientChartId");
      window.sessionStorage.removeItem("externalId");
      window.sessionStorage.removeItem("patChartId");
      this.navSvc.goToInternalPage("dashboard-page", {});
    }
  }

  onChange(orgId: string) {
    this.utilityService.increaseSpinnerCount("Changing organization...");
    this.cleanUp();
    // need to update the currentOrgId of the user to selected org.
    this.userProfileService.updateCurrentOrg(orgId).subscribe(
      (result) => {
        if (result) {
          // Signal interceptor to queue HTTP requests during token refresh
          this.stateData.beginManualTokenRefresh();

          // need to update the token with the selected organization, it can be company or location
          this.authSvc
            .refreshAccessToken(true)
            .then((newToken) => {
              // Release queued HTTP requests with the new token
              this.stateData.endManualTokenRefresh(newToken);
              this.utilityService.decreaseSpinnerCount();

              // if its company, navigate to that company details, else navigate to the dashboard
              let isCompany = this.organizations.find(
                (f) => f.orgId === orgId && f.orgType === "COMPANY"
              );
              this.selectedOrgType = isCompany ? "COMPANY" : "LOCATION";
              let user = this.authSvc.getUser();
              user.organizations = this.organizations;
              this.authSvc.setUser(user);
              this.authSvc.setLoggedInUser(user);
              this.authSvc.UserInfoChange.next(user);
              this.showNavbar = true;
              this.loadFilters(user);
              this.getLocationTimeZone();
              this.deleteLocalStorageToggles();
              this.navBarServiceProvider.featureToggleData = null;
              setTimeout(() => {
                this.events.publish("user:updateorganizations", user);
                this.events.publish("user:InDashboardPage");
              }, 0);
              if (isCompany) {
                // route to company detail screen
                this.navSvc.goToInternalPage("CompanyDetailsPage", {});
              } else {
                // route to dashboard
                this.sendDashboard();
              }
            })
            .catch((error) => {
              this.stateData.endManualTokenRefresh(null);
              this.utilityService.decreaseSpinnerCount();
              console.error("Token refresh failed during org change:", error);

              this.authSvc.signOut();
            });
        } else {
          this.utilityService.decreaseSpinnerCount();
          this.authSvc.signOut();
        }
      },
      (err) => {
        this.utilityService.decreaseSpinnerCount();
        console.error("updateCurrentOrg failed:", err);
        this.authSvc.signOut();
      }
    );
  }

  deleteLocalStorageToggles() {
    const items = Object.keys(localStorage);
    items.forEach((key) => {
      if (key.startsWith("ft-")) {
        localStorage.removeItem(key);
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/navigation/components/navigation-header/navigation-header.ts